import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from '../Info';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { ReactComponent as EditPencilIcon } from '../../../../assets/images/EditPencil.svg';
import { ReactComponent as Visible } from '../../../../assets/images/ShowContent.svg';
import { ReactComponent as Hide } from '../../../../assets/images/HideContent.svg';
import { ReactComponent as Copy } from '../../../../assets/images/CopyContent.svg';
import { ReactComponent as Check } from '../../../../assets/images/ArrowSaveEmail.svg';
import { ButtonClassNames } from '../../../../models/Common';
import { InfoItem } from '../../../../pages/ServiceManagement';

interface BrizTvInfoProps {
  email: string;
  handleChangeEmail: (iptvId: string | number) => void;
  password: string;
  handleChangePassword: (iptvId: string | number) => void;
  phone: string;
  handleChangePhone: (iptvId: string | number) => void;
  iptvId: string | number;
  isEditingBrizTvAvailable: boolean;
  handleShowPassword: () => void;
  isShownPassword: boolean;
}

export const BrizTvInfo: FC<BrizTvInfoProps> = ({
  email,
  handleChangeEmail,
  password,
  handleChangePassword,
  phone,
  handleChangePhone,
  iptvId,
  isEditingBrizTvAvailable,
  handleShowPassword,
  isShownPassword,
}) => {
  const { t } = useTranslation();
  const [isEmailCopied, setIsEmailCopied] = useState(false);
  const copyResetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyEmail = async () => {
    if (!email || isEmailCopied) return;

    await navigator.clipboard.writeText(email);
    setIsEmailCopied(true);

    if (copyResetTimeout.current) {
      clearTimeout(copyResetTimeout.current);
    }

    copyResetTimeout.current = setTimeout(() => {
      setIsEmailCopied(false);
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (copyResetTimeout.current) {
        clearTimeout(copyResetTimeout.current);
      }
    };
  }, []);

  const brizTvLoginDetails: InfoItem[] = [
    {
      label: t('serviceManagement.emailAddress'),
      value: email,
      button: (
        <CustomMUIButton
          label={<EditPencilIcon />}
          handleClick={() => handleChangeEmail(iptvId)}
          className={ButtonClassNames.IconFulfilled}
          tooltipText={`${t('tooltip.change')}`}
          isDisabled={isEditingBrizTvAvailable}
        />
      ),
      buttonShowPassword: (
        <CustomMUIButton
          label={isEmailCopied ? <Check /> : <Copy />}
          handleClick={handleCopyEmail}
          className={ButtonClassNames.IconFulfilled}
          tooltipText={isEmailCopied ? `${t('tooltip.emailCopied')}` : `${t('tooltip.copyEmail')}`}
          isDisabled={isEditingBrizTvAvailable}
        />
      ),
    },
    {
      label: t('common.password'),
      value: isShownPassword ? password : '**********',
      button: (
        <CustomMUIButton
          label={<EditPencilIcon />}
          handleClick={() => handleChangePassword(iptvId)}
          className={ButtonClassNames.IconFulfilled}
          tooltipText={`${t('tooltip.change')}`}
          isDisabled={isEditingBrizTvAvailable}
        />
      ),
      buttonShowPassword: (
        <CustomMUIButton
          label={isShownPassword ? <Hide /> : <Visible />}
          handleClick={() => handleShowPassword()}
          className={ButtonClassNames.IconFulfilled}
          tooltipText={isShownPassword ? `${t('tooltip.hidePassword')}` : `${t('tooltip.showPassword')}`}
          isDisabled={isEditingBrizTvAvailable}
        />
      ),
    },
    {
      label: t('serviceManagement.phoneNumber'),
      value: phone,
      button: (
        <CustomMUIButton
          label={<EditPencilIcon />}
          handleClick={() => handleChangePhone(iptvId)}
          className={ButtonClassNames.IconFulfilled}
          tooltipText={`${t('tooltip.change')}`}
          isDisabled={isEditingBrizTvAvailable}
        />
      ),
    },
  ];
  return <Info infoItems={brizTvLoginDetails} title={t('serviceManagement.brizTvLoginDetails')} />;
};
